import { io, type Socket } from 'socket.io-client';
import {
  DEFAULT_THEME,
  LIMITS,
  THEMES,
  type ClientToServerEvents,
  type ServerToClientEvents,
  type SessionView,
  type ThemeView,
} from '@dreamquiz/shared';
import './styles.css';
import { AdminPanel } from './adminPanel.js';
import { BillingPanel } from './billingPanel.js';
import { runBootSequence } from './boot.js';
import { Scoreboard } from './scoreboard.js';
import { sound } from './sound.js';
import { TerminalView } from './terminal.js';
import { tts } from './tts.js';

const CONSENT_KEY = 'dq.consent.v1';

type DqSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

interface Bootstrap {
  protocolVersion: number;
  authMode: 'dev' | 'dreamid';
  user: { name: string; handle: string } | null;
  theme: ThemeView;
  firstVisit: boolean;
  sshPort: number | null;
}

function must<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Element #${id} fehlt im HTML`);
  return el as T;
}

const dq = must('dq');
const out = must('out');
const input = must<HTMLInputElement>('in');
const termBox = must('term');
const conn = must('conn');
const who = must('who');
const volBtn = must<HTMLButtonElement>('vol-btn');
const volSlider = must<HTMLInputElement>('vol-slider');
const ttsBtn = must<HTMLButtonElement>('tts-btn');

const term = new TerminalView(out, input, termBox);
const scoreboard = new Scoreboard(must('sb'));

let socket: DqSocket | null = null;
let bootDone = false;

const adminPanel = new AdminPanel({
  setProvider: (provider) => socket?.emit('admin:set-provider', { provider }),
  setModel: (model) => socket?.emit('admin:set-model', { model }),
  setKey: (provider, key) => socket?.emit('admin:set-key', { provider, key }),
  addAdmin: (handle) => socket?.emit('admin:add-admin', { handle }),
  removeAdmin: (handle) => socket?.emit('admin:remove-admin', { handle }),
  refresh: () => socket?.emit('admin:refresh'),
  setBillingEnabled: (enabled) => socket?.emit('admin:billing-set-enabled', { enabled }),
  saveBillingPackage: (input) => socket?.emit('admin:billing-save-package', input),
  deleteBillingPackage: (id) => socket?.emit('admin:billing-delete-package', { id }),
  saveBillingPlan: (input) => socket?.emit('admin:billing-save-plan', input),
  deleteBillingPlan: (id) => socket?.emit('admin:billing-delete-plan', { id }),
  setBillingStatsRange: (range) => socket?.emit('admin:billing-stats', { range }),
  setHostingCost: (cents) => socket?.emit('admin:billing-set-hosting-cost', { cents }),
});

const billingPanel = new BillingPanel({
  refresh: () => socket?.emit('billing:refresh'),
  buyPackage: (packageId) => socket?.emit('billing:buy-package', { packageId }),
  buyPlan: (planId) => socket?.emit('billing:buy-plan', { planId }),
  cancelSubscription: () => socket?.emit('billing:cancel-subscription'),
});

must<HTMLButtonElement>('billing-btn').addEventListener('click', () => billingPanel.open());

/* ------------------------------------------------------------------ Theme */

function applyTheme(theme: ThemeView): void {
  dq.style.setProperty('--ac', theme.accent);
  dq.style.setProperty('--ac2', theme.accent2);
}

/* ------------------------------------------------------------ Lautstärke */

function syncVolumeUi(): void {
  volSlider.value = String(sound.percent);
  volBtn.textContent = sound.isMuted || sound.percent === 0 ? '🔇' : '🔊';
  volBtn.setAttribute('aria-pressed', String(sound.isMuted));
}

volSlider.addEventListener('input', () => {
  sound.setVolume(Number(volSlider.value));
  syncVolumeUi();
});
volBtn.addEventListener('click', () => {
  sound.toggleMute();
  syncVolumeUi();
});
syncVolumeUi();

/* ------------------------------------------------------------- Vorlesen */

function syncTtsUi(): void {
  ttsBtn.classList.toggle('on', tts.enabled);
  ttsBtn.setAttribute('aria-pressed', String(tts.enabled));
}

if (!tts.supported) {
  // Kein Speech-Synthesis - meist Mobilgeräte/ältere Browser. Kein Knopf für
  // eine Funktion, die ohnehin nicht wirkt, statt sie vorzutäuschen.
  ttsBtn.hidden = true;
} else {
  const ttsOverlay = must('tts-overlay');
  const ttsEnabledCheckbox = must<HTMLInputElement>('tts-enabled');
  const ttsVoiceSelect = must<HTMLSelectElement>('tts-voice');
  const ttsVolumeSlider = must<HTMLInputElement>('tts-volume');

  function populateVoices(): void {
    const all = tts.getVoices();
    // Der Fragetext ist immer Deutsch - eine Stimme in anderer Sprache würde
    // ihn falsch aussprechen. Nur wenn wirklich keine deutsche Stimme
    // installiert ist (selten), lieber alle zeigen als eine leere Liste.
    const german = all.filter((v) => v.lang.toLowerCase().startsWith('de'));
    const voices = german.length ? german : all;

    const previous = ttsVoiceSelect.value;
    ttsVoiceSelect.replaceChildren();
    const defaultOption = document.createElement('option');
    defaultOption.value = '';
    defaultOption.textContent = 'Standard (Browser wählt)';
    ttsVoiceSelect.appendChild(defaultOption);
    for (const voice of voices) {
      const option = document.createElement('option');
      option.value = voice.voiceURI;
      option.textContent = voice.name;
      ttsVoiceSelect.appendChild(option);
    }
    ttsVoiceSelect.value = tts.voiceURI && voices.some((v) => v.voiceURI === tts.voiceURI) ? tts.voiceURI : previous || '';
  }

  // Manche Browser (u. a. Chrome) liefern getVoices() erst nach diesem Event
  // gefüllt - beim ersten Aufruf direkt nach dem Laden oft noch leer.
  tts.onVoicesChanged(populateVoices);
  populateVoices();

  ttsEnabledCheckbox.checked = tts.enabled;
  ttsVolumeSlider.value = String(tts.volumePercent);

  ttsEnabledCheckbox.addEventListener('change', () => {
    tts.setEnabled(ttsEnabledCheckbox.checked);
    syncTtsUi();
  });
  ttsVoiceSelect.addEventListener('change', () => {
    tts.setVoice(ttsVoiceSelect.value || null);
  });
  ttsVolumeSlider.addEventListener('input', () => {
    tts.setVolume(Number(ttsVolumeSlider.value));
  });

  ttsBtn.addEventListener('click', () => {
    ttsOverlay.hidden = false;
  });
  must('tts-close').addEventListener('click', () => {
    ttsOverlay.hidden = true;
  });
  ttsOverlay.addEventListener('click', (event) => {
    if (event.target === ttsOverlay) ttsOverlay.hidden = true;
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !ttsOverlay.hidden) ttsOverlay.hidden = true;
  });

  syncTtsUi();
}

/* -------------------------------------------------------------- Infos */

function wireInfoOverlay(triggerId: string, overlayId: string, closeId: string): void {
  const trigger = must<HTMLButtonElement>(triggerId);
  const overlay = must(overlayId);
  const close = must<HTMLButtonElement>(closeId);

  trigger.addEventListener('click', () => {
    overlay.hidden = false;
  });
  close.addEventListener('click', () => {
    overlay.hidden = true;
  });
  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) overlay.hidden = true;
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !overlay.hidden) overlay.hidden = true;
  });
}

wireInfoOverlay('help-btn', 'help-overlay', 'help-close');
wireInfoOverlay('ssh-btn', 'ssh-overlay', 'ssh-close');

/** Zeigt den echten Host/Port im "!"-Infofenster, sobald bekannt (Wunsch Niggo: SSH-Anleitung). */
function fillSshInstructions(sshPort: number | null): void {
  const el = document.getElementById('ssh-connect-cmd');
  if (!el) return;
  if (sshPort === null) {
    el.textContent = 'SSH ist auf diesem Server aktuell deaktiviert.';
    return;
  }
  el.textContent = `ssh -p ${sshPort} ${window.location.hostname}`;
}

/* ---------------------------------------------------------- Cookie-Banner */

function consentGiven(): boolean {
  try {
    return localStorage.getItem(CONSENT_KEY) === '1';
  } catch {
    return false;
  }
}

function rememberConsent(): void {
  try {
    localStorage.setItem(CONSENT_KEY, '1');
  } catch {
    /* Privater Modus - dann fragt der Banner beim nächsten Mal erneut. */
  }
}

function askForConsent(): Promise<void> {
  const overlay = must('consent');
  const button = must<HTMLButtonElement>('consent-ok');
  overlay.hidden = false;
  button.focus();

  return new Promise((resolve) => {
    button.addEventListener(
      'click',
      () => {
        rememberConsent();
        overlay.hidden = true;
        resolve();
      },
      { once: true },
    );
  });
}

/* ------------------------------------------------------------------ Start */

async function start(): Promise<void> {
  applyTheme(THEMES[DEFAULT_THEME]!);
  scoreboard.render({
    mode: 'idle',
    title: 'scoreboard',
    subtitle: 'idle',
    players: [],
    hint: 'Noch kein Spiel.\nStarte links eine Runde 👈',
  });

  const connecting = term.local('Verbindung wird aufgebaut …', 'muted');
  connecting.classList.add('blink');

  if (!consentGiven()) await askForConsent();

  let boot: Bootstrap;
  try {
    const response = await fetch('/api/bootstrap', { credentials: 'include' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    boot = (await response.json()) as Bootstrap;
  } catch {
    connecting.classList.remove('blink');
    term.local('[FEHLER] Server nicht erreichbar. Läuft er? (npm run dev)', 'error');
    return;
  }

  applyTheme(boot.theme);
  fillSshInstructions(boot.sshPort);
  connecting.classList.remove('blink');
  term.local('[OK] Verbunden', 'ok');

  socket = io({ withCredentials: true, transports: ['websocket', 'polling'] });
  wireSocket(socket, boot);
}

function wireSocket(sock: DqSocket, boot: Bootstrap): void {
  sock.on('connect', () => {
    conn.textContent = 'online';
    conn.classList.add('on');

    if (bootDone) {
      // Wiederverbindung: Server baut den aktuellen Bildschirm neu auf.
      sock.emit('client:ready');
      return;
    }

    void runBootSequence(term, { full: boot.firstVisit }).then(() => {
      bootDone = true;
      term.setEnabled(true);
      sock.emit('client:ready');
    });
  });

  sock.on('disconnect', () => {
    conn.textContent = 'offline';
    conn.classList.remove('on');
    term.local('[!] Verbindung verloren - versuche neu zu verbinden …', 'warn');
  });

  sock.on('term:lines', ({ lines }) => term.print(lines));
  sock.on('term:replace', ({ id, line }) => term.replace(id, line));
  sock.on('term:clear', () => term.clear());
  sock.on('term:input-mode', ({ enabled, masked }) => {
    term.setEnabled(enabled);
    term.setMasked(Boolean(masked));
  });
  sock.on('ui:scoreboard', (view) => scoreboard.render(view));
  sock.on('ui:theme', (theme) => applyTheme(theme));
  sock.on('session:user', (view) => showUser(view));
  sock.on('ui:open', ({ url, target }) => {
    if (target === 'popup') {
      window.open(url, 'dreamid-login', 'width=520,height=680');
    } else {
      window.open(url, '_blank', 'noopener');
    }
  });
  sock.on('term:complete:result', ({ value, matches }) => {
    input.value = value;
    if (matches.length > 1) term.local(matches.join('   '), 'muted');
  });
  sock.on('ui:sound', ({ cue }) => sound.play(cue));
  sock.on('ui:question', ({ prompt }) => tts.speak(prompt));
  sock.on('ui:admin-panel', ({ data, open }) => {
    adminPanel.render(data);
    if (open) adminPanel.open();
  });
  sock.on('ui:billing-panel', ({ data, open }) => {
    billingPanel.render(data);
    if (open) billingPanel.open();
  });
}

function showUser(view: SessionView): void {
  if (view.user) {
    who.textContent = `${view.user.handle}${view.authMode === 'dev' ? ' (dev)' : ''}`;
    who.classList.add('on');
    must<HTMLButtonElement>('billing-btn').hidden = false;
  } else {
    who.textContent = 'nicht angemeldet';
    who.classList.remove('on');
    must<HTMLButtonElement>('billing-btn').hidden = true;
  }
}

/* ---------------------------------------------------------------- Eingabe */

const MAX_HISTORY = 50;
const inputHistory: string[] = [];
let historyIndex: number | null = null;
let draftBeforeHistory = '';

function setCursorToEnd(): void {
  const pos = input.value.length;
  requestAnimationFrame(() => input.setSelectionRange(pos, pos));
}

input.addEventListener('keydown', (event) => {
  if (event.key === 'Backspace') sound.play('backspace');
  else if (event.key.length === 1) sound.play('type');

  if (event.key === 'Enter') {
    event.preventDefault();
    const value = input.value.slice(0, LIMITS.inputLength);
    input.value = '';
    historyIndex = null;
    if (!value.trim()) return;

    if (inputHistory[inputHistory.length - 1] !== value) {
      inputHistory.push(value);
      if (inputHistory.length > MAX_HISTORY) inputHistory.shift();
    }

    // Der Client entscheidet nichts - er reicht die Zeile nur weiter.
    socket?.emit('term:input', { line: value });
    return;
  }

  if (event.key === 'ArrowUp') {
    if (!inputHistory.length) return;
    event.preventDefault();
    if (historyIndex === null) {
      draftBeforeHistory = input.value;
      historyIndex = inputHistory.length - 1;
    } else if (historyIndex > 0) {
      historyIndex--;
    }
    input.value = inputHistory[historyIndex]!;
    setCursorToEnd();
    return;
  }

  if (event.key === 'ArrowDown') {
    if (historyIndex === null) return;
    event.preventDefault();
    historyIndex++;
    if (historyIndex >= inputHistory.length) {
      historyIndex = null;
      input.value = draftBeforeHistory;
    } else {
      input.value = inputHistory[historyIndex]!;
    }
    setCursorToEnd();
    return;
  }

  if (event.key === 'Tab') {
    event.preventDefault();
    socket?.emit('term:complete', { partial: input.value });
    return;
  }
});

// Echte Tastatureingaben (nicht die programmatischen Zuweisungen oben) brechen
// die Verlaufs-Navigation ab - von da an ist es wieder eine normale, neue Zeile.
input.addEventListener('input', () => {
  historyIndex = null;
});

/* -------------------------------------------------------- DreamID-Popup */

// Das Callback-Popup (server/src/auth/callbackPage.ts) meldet sich hier,
// nachdem der Login-Zustand serverseitig schon in der Sitzung steckt - ein
// erneutes client:ready holt ihn ab, ohne die Boot-Animation zu wiederholen
// (genau der Reconnect-Pfad, den es dafür schon gibt).
window.addEventListener('message', (event) => {
  if (event.origin !== window.location.origin) return;
  const data = event.data as { type?: string; ok?: boolean; message?: string } | null;

  if (data?.type === 'dreamid-login') {
    if (data.ok) {
      socket?.emit('client:ready');
    } else {
      term.local(`DreamID-Anmeldung fehlgeschlagen: ${data.message ?? 'unbekannter Fehler'}`, 'error');
    }
    return;
  }

  // Stripe-Checkout-Popup (server/src/billing/successPage.ts) meldet sich
  // hier nach Kauf/Abo-Abschluss - dasselbe postMessage-Muster wie beim
  // DreamID-Login oben. Guthaben/Abo-Status im Panel neu anfragen, egal ob
  // es gerade offen ist (schadet nicht, ist billig).
  if (data?.type === 'stripe-checkout') {
    if (!data.ok) term.local(`Zahlung fehlgeschlagen: ${data.message ?? 'unbekannter Fehler'}`, 'error');
    socket?.emit('billing:refresh');
  }
});

void start();
